// frontend/src/hooks/useCorrespondences.ts - UPDATED to match new API and hook structure
// NEW FUNCTION: Updated correspondences hooks with new API structure

import { useCallback } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  validateObjectId, 
  validateObjectIds, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,

  // Correspondence types
  Correspondence,
  CorrespondenceQueryParams,
  CorrespondenceCreateRequest,
  CorrespondenceUpdateRequest,

  // V3: Correspondence split model types
  CorrespondenceEmail,
  CorrespondenceAttachment,
  CorrespondenceAI,
  CorrespondenceAudit,
  CorrespondenceWithRelated,
  CorrespondenceFull,
  CorrespondenceV3Options,

  // Response types
  Response,
  ResponseQueryParams,
  ResponseCreateRequest,

  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== LOCAL INTERFACES =====

/**
 * Local interface for custom fields
 */
interface SimpleCustomFieldSchema {
  name: string;
  value: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'url';
}

/**
 * Extended correspondence creation data with custom fields
 */
interface ExtendedCorrespondenceCreateRequest extends CorrespondenceCreateRequest {
  tags?: string[];
  customFields?: SimpleCustomFieldSchema[];
  documentType?: 'email' | 'letter' | 'memo' | 'report' | 'contract' | 'invoice' | 'form' | 'other';
  documentReference?: string;
  aiSummarization?: { enabled: boolean };
  enableOCRProcessing?: boolean;
  enableVirusScanning?: boolean;
}

/**
 * Extended correspondence update data
 */
interface ExtendedCorrespondenceUpdateRequest extends CorrespondenceUpdateRequest {
  updateNotes?: string;
  preserveHistory?: boolean;
  tags?: string[];
  customFields?: SimpleCustomFieldSchema[];
}

// ===== CORRESPONDENCE QUERY HOOKS =====

/**
 * Fetch paginated list of correspondences with comprehensive filtering
 */
export const useCorrespondences = (params?: CorrespondenceQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Correspondence>> => {
      
      const response = await apiClient.correspondences.list(params);
      
      return normalizePaginatedResponse<Correspondence>(response);
    },
    {
      immediate: true,
      dependencies: params ? [JSON.stringify(params)] : [],
      retryCount: 2,
      retryDelay: 1000,
      rateLimitKey: 'correspondences_list_v2',
      ...options
    }
  );
};

/**
 * Fetch single correspondence by ID with comprehensive details
 */
export const useCorrespondence = (id?: string, options?: UseApiOptions & { includeAttachments?: boolean; includeEmail?: boolean; includeAI?: boolean; includeAudit?: boolean }) => {
  const { includeAttachments = true, includeEmail = false, includeAI = false, includeAudit = false, ...apiOptions } = options || {};

  return useApi(
    async (): Promise<Correspondence> => {
      validateObjectId(id, 'Correspondence ID');


      const response = await apiClient.correspondences.get(id!, {
        includeAttachments,
        includeEmail,
        includeAI,
        includeAudit
      });

      return normalizeSingleResponse<Correspondence>(response);
    },
    {
      immediate: !!id,
      rateLimitKey: `correspondence_v2_${id}`,
      ...apiOptions,
    }
  );
};

/**
 * Advanced correspondences search hook with comprehensive v2.0 capabilities
 */
export const useCorrespondencesSearch = (searchQuery?: string, searchParams?: CorrespondenceQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Correspondence>> => {
      if (!searchQuery?.trim()) {
        return {
          data: [],
          pagination: {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
            hasNextPage: false,
            hasPrevPage: false
          },
          total: 0,
          totalPages: 0,
          page: 1,
          limit: 10
        };
      }
      
      
      const response = await apiClient.correspondences.search(searchQuery, searchParams);
      
      return normalizePaginatedResponse<Correspondence>(response);
    },
    {
      immediate: !!searchQuery?.trim(),
      dependencies: [searchQuery, JSON.stringify(searchParams || {})],
      debounce: 500, // Debounce search queries
      rateLimitKey: 'correspondences_search_v2',
      ...options
    }
  );
};

/**
 * Get correspondence responses
 */
export const useCorrespondenceResponses = (correspondenceId?: string, params?: ResponseQueryParams, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<PaginatedResponse<Response>> => {
      validateObjectId(correspondenceId, 'Correspondence ID');
      
      
      const response = await apiClient.correspondences.getResponses(correspondenceId!, params);
      
      return normalizePaginatedResponse<Response>(response);
    },
    {
      immediate: !!correspondenceId,
      dependencies: [correspondenceId, JSON.stringify(params || {})],
      rateLimitKey: `correspondence_responses_${correspondenceId}`,
      ...options
    }
  );
};

/**
 * Get correspondence analytics
 */
export const useCorrespondenceAnalytics = (params?: any, options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      
      const response = await apiClient.correspondences.getAnalytics(params);
      
      return normalizeSingleResponse(response);
    },
    {
      immediate: true,
      dependencies: [JSON.stringify(params || {})],
      rateLimitKey: 'correspondence_analytics_v2',
      ...options
    }
  );
};

// ===== CORRESPONDENCE MUTATION HOOKS =====

/**
 * Create correspondence with enhanced validation and features
 */
export const useCreateCorrespondence = (options?: UseMutationOptions<Correspondence, ExtendedCorrespondenceCreateRequest>) => {
  return useMutation(
    async (data: ExtendedCorrespondenceCreateRequest): Promise<Correspondence> => {
      
      // Enhanced client-side validation
      if (data.sourceEmailIds && data.sourceEmailIds.length > 0) {
        data.sourceEmailIds.forEach((emailId: string, index: number) => {
          validateObjectId(emailId, `Source Email ${index + 1} ID`);
        });
      }
      if (data.projectId) {
        validateObjectId(data.projectId, 'Project ID');
      }
      if (data.visibilityGroups && data.visibilityGroups.length > 0) {
        data.visibilityGroups.forEach((groupId: string, index: number) => {
          validateObjectId(groupId, `Visibility Group ${index + 1} ID`);
        });
      }

      // Validate custom fields if they exist
      if (data.customFields && Array.isArray(data.customFields) && data.customFields.length > 0) {
        data.customFields.forEach((field, index) => {
          if (!field.name || !field.name.trim()) {
            throw new Error(`Custom field ${index + 1} must have a name`);
          }
          if (!['text', 'number', 'date', 'boolean', 'url'].includes(field.type)) {
            throw new Error(`Custom field ${index + 1} has invalid type: ${field.type}`);
          }
        });
      }

      // Validate tags if they exist
      if (data.tags && Array.isArray(data.tags) && data.tags.length > 0) {
        data.tags.forEach((tag: string, index: number) => {
          if (typeof tag !== 'string' || tag.trim().length === 0) {
            throw new Error(`Tag ${index + 1} must be a non-empty string`);
          }
          if (tag.length > 50) {
            throw new Error(`Tag ${index + 1} cannot exceed 50 characters`);
          }
        });
      }
      
      // Remove extended fields for API call
      const { tags, customFields, documentType, documentReference, aiSummarization, enableOCRProcessing, enableVirusScanning, ...apiData } = data;
      
      const response = await apiClient.correspondences.create(apiData);
      
      return normalizeSingleResponse<Correspondence>(response);
    }, 
    options
  );
};

/**
 * Create correspondence from email with comprehensive processing
 */
export const useCreateCorrespondenceFromEmail = (options?: UseMutationOptions<Correspondence, {
  emailId: string;
  projectId?: string;
  notes?: string;
  preserveEmail?: boolean;
  documentType?: 'email' | 'letter' | 'memo' | 'report' | 'contract' | 'invoice' | 'form' | 'other';
  documentReference?: string;
  tags?: string[];
  customFields?: SimpleCustomFieldSchema[];
  aiSummarization?: { enabled: boolean };
  enableOCRProcessing?: boolean;
  enableVirusScanning?: boolean;
}>) => {
  return useMutation(
    async (data): Promise<Correspondence> => {
      
      validateObjectId(data.emailId, 'Email ID');
      if (data.projectId) {
        validateObjectId(data.projectId, 'Project ID');
      }
      
      // Create correspondence with email reference
      const correspondenceData: CorrespondenceCreateRequest = {
        subject: data.notes || 'Correspondence from Email',
        body: data.notes || '',
        sourceEmailIds: [data.emailId],
        sourceType: 'email',
        projectId: data.projectId,
        visibilityGroups: [],
        manual: false
      };
      
      const response = await apiClient.correspondences.create(correspondenceData);
      
      return normalizeSingleResponse<Correspondence>(response);
    }, 
    options
  );
};

/**
 * Update correspondence with version control
 */
export const useUpdateCorrespondence = (options?: UseMutationOptions<Correspondence, { 
  id: string; 
  data: ExtendedCorrespondenceUpdateRequest;
}>) => {
  return useMutation(
    async ({ id, data }): Promise<Correspondence> => {
      validateObjectId(id, 'Correspondence ID');
      
      
      // Validate project ID if provided
      if (data.projectId) {
        validateObjectId(data.projectId, 'Project ID');
      }
      
      // Validate visibility groups if provided
      if (data.visibilityGroups && data.visibilityGroups.length > 0) {
        data.visibilityGroups.forEach((groupId: string, index: number) => {
          validateObjectId(groupId, `Visibility Group ${index + 1} ID`);
        });
      }
      
      // Remove extended fields for API call (keep customFields - needed for field updates)
      const { updateNotes, preserveHistory, tags, ...apiData } = data;
      
      const response = await apiClient.correspondences.update(id, apiData);
      
      return normalizeSingleResponse<Correspondence>(response);
    },
    options
  );
};

/**
 * Delete correspondence
 */
export const useDeleteCorrespondence = (options?: UseMutationOptions<void, string>) => {
  return useMutation(
    async (id: string): Promise<void> => {
      validateObjectId(id, 'Correspondence ID');
      
      
      const response = await apiClient.correspondences.delete(id);
      
      return normalizeSingleResponse<void>(response);
    },
    options
  );
};

/**
 * Archive correspondence
 */
export const useArchiveCorrespondence = (options?: UseMutationOptions<Correspondence, string>) => {
  return useMutation(
    async (id: string): Promise<Correspondence> => {
      validateObjectId(id, 'Correspondence ID');
      
      
      const response = await apiClient.correspondences.archive(id);
      
      return normalizeSingleResponse<Correspondence>(response);
    },
    options
  );
};

/**
 * Classify correspondence
 */
export const useClassifyCorrespondence = (options?: UseMutationOptions<any, string>) => {
  return useMutation(
    async (id: string): Promise<any> => {
      validateObjectId(id, 'Correspondence ID');
      
      
      const response = await apiClient.correspondences.classify(id);
      
      return normalizeSingleResponse(response);
    },
    options
  );
};

/**
 * Reclassify correspondence to different project
 */
export const useReclassifyCorrespondence = (options?: UseMutationOptions<Correspondence, { id: string; projectId: string }>) => {
  return useMutation(
    async ({ id, projectId }): Promise<Correspondence> => {
      validateObjectId(id, 'Correspondence ID');
      validateObjectId(projectId, 'Project ID');
      
      
      const response = await apiClient.correspondences.reclassify(id, projectId);
      
      return normalizeSingleResponse<Correspondence>(response);
    },
    options
  );
};

/**
 * Create response to correspondence
 */
export const useCreateCorrespondenceResponse = (options?: UseMutationOptions<Response, { id: string; data: ResponseCreateRequest }>) => {
  return useMutation(
    async ({ id, data }): Promise<Response> => {
      validateObjectId(id, 'Correspondence ID');
      
      
      // Validate required response fields
      if (!data.mailBody || typeof data.mailBody !== 'string' || data.mailBody.trim().length === 0) {
        throw new Error('Response content is required');
      }
      
      const response = await apiClient.correspondences.createResponse(id, data);
      
      return normalizeSingleResponse<Response>(response);
    },
    options
  );
};

// ===== BATCH OPERATIONS =====

/**
 * Batch operations for correspondences
 */
export const useCorrespondenceBatchOperations = (options?: UseMutationOptions<any, {
  operation: 'delete' | 'archive' | 'tag' | 'move-project' | 'reprocess-ai' | 'reprocess-ocr';
  correspondenceIds: string[];
  options?: {
    projectId?: string;
    tags?: string[];
    deleteReason?: string;
    forceAIReprocessing?: boolean;
    enableVirusRescan?: boolean;
  };
}>) => {
  return useMutation(
    async (data): Promise<any> => {
      if (!data.correspondenceIds || data.correspondenceIds.length === 0) {
        throw new Error('At least one correspondence ID is required');
      }
      
      // Validate all correspondence IDs
      data.correspondenceIds.forEach((id, index) => {
        validateObjectId(id, `Correspondence ${index + 1} ID`);
      });
      
      // Validate project ID if moving to project
      if (data.operation === 'move-project' && data.options?.projectId) {
        validateObjectId(data.options.projectId, 'Target Project ID');
      }
      
      
      // Use existing batch operations
      if (data.operation === 'delete') {
        const response = await apiClient.correspondences.batchDelete(data.correspondenceIds);
        return normalizeSingleResponse(response);
      } else if (data.operation === 'move-project' && data.options?.projectId) {
        const response = await apiClient.correspondences.batchClassify(data.correspondenceIds, data.options.projectId);
        return normalizeSingleResponse(response);
      } else {
        console.warn(`Batch operation '${data.operation}' not implemented yet`);
        return { message: `Batch ${data.operation} requested for ${data.correspondenceIds.length} items` };
      }
    },
    options
  );
};

// ===== PLACEHOLDER HOOKS FOR FUTURE FEATURES =====

/**
 * Correspondence audit trail hook
 * Note: This would need to be implemented as a custom endpoint
 */
export const useCorrespondenceAuditTrail = (correspondenceId?: string, params?: {
  page?: number;
  limit?: number;
  actionType?: string;
  dateFrom?: string;
  dateTo?: string;
  performedBy?: string;
}, options?: UseApiOptions) => {
  return useApi(
    async () => {
      validateObjectId(correspondenceId, 'Correspondence ID');
      
      console.warn('Audit trail endpoint not yet implemented');
      // Placeholder - would need custom implementation
      return { 
        message: 'Audit trail not yet implemented', 
        correspondenceId,
        params 
      };
    },
    {
      immediate: !!correspondenceId,
      dependencies: [correspondenceId, JSON.stringify(params || {})],
      rateLimitKey: `correspondence_audit_${correspondenceId}`,
      ...options
    }
  );
};

/**
 * Correspondence version history hook
 * Note: This would need to be implemented as a custom endpoint
 */
export const useCorrespondenceVersionHistory = (correspondenceId?: string, options?: UseApiOptions) => {
  return useApi(
    async () => {
      validateObjectId(correspondenceId, 'Correspondence ID');
      
      console.warn('Version history endpoint not yet implemented');
      // Placeholder - would need custom implementation
      return { 
        message: 'Version history not yet implemented', 
        correspondenceId 
      };
    },
    {
      immediate: !!correspondenceId,
      rateLimitKey: `correspondence_versions_${correspondenceId}`,
      ...options
    }
  );
};

/**
 * Custom fields validation hook
 * Note: This would need a custom implementation
 */
export const useValidateCustomFields = () => {
  return useMutation(
    async (customFields: SimpleCustomFieldSchema[]) => {
      console.warn('Custom fields validation not yet implemented');
      // Simple client-side validation
      customFields.forEach((field, index) => {
        if (!field.name || !field.name.trim()) {
          throw new Error(`Custom field ${index + 1} must have a name`);
        }
        if (!['text', 'number', 'date', 'boolean', 'url'].includes(field.type)) {
          throw new Error(`Custom field ${index + 1} has invalid type: ${field.type}`);
        }
      });
      
      return {
        valid: true,
        fields: customFields.length,
        message: 'Custom fields validation passed'
      };
    }
  );
};

// ===== V3: CORRESPONDENCE SPLIT MODEL HOOKS =====

/**
 * V3: Get a single correspondence with optional lazy-loaded related data
 * @param id - Correspondence ID
 * @param options - Options for lazy loading (includeEmail, includeAttachments, includeAI, includeAudit)
 */
export const useCorrespondenceV3 = (
  id: string | null,
  options?: CorrespondenceV3Options,
  hookOptions?: UseApiOptions
) => {
  return useApi<CorrespondenceWithRelated>(
    async () => {
      if (!id) {
        throw new Error('Correspondence ID is required');
      }

      validateObjectId(id, 'Correspondence ID');


      const response = await apiClient.correspondences.getV3(id, options);

      return normalizeSingleResponse<CorrespondenceWithRelated>(response);
    },
    {
      immediate: !!id,
      dependencies: [id, JSON.stringify(options)],
      rateLimitKey: `correspondence_v3_${id}`,
      ...hookOptions
    }
  );
};

/**
 * V3: Get email content for a correspondence
 * @param id - Correspondence ID
 */
export const useCorrespondenceEmail = (
  id: string | null,
  hookOptions?: UseApiOptions
) => {
  return useApi<CorrespondenceEmail>(
    async () => {
      if (!id) {
        throw new Error('Correspondence ID is required');
      }

      validateObjectId(id, 'Correspondence ID');


      const response = await apiClient.correspondences.getEmail(id);

      return normalizeSingleResponse<CorrespondenceEmail>(response);
    },
    {
      immediate: !!id,
      dependencies: [id],
      rateLimitKey: `correspondence_email_${id}`,
      ...hookOptions
    }
  );
};

/**
 * V3: Get attachments for a correspondence
 * @param id - Correspondence ID
 */
export const useCorrespondenceAttachments = (
  id: string | null,
  hookOptions?: UseApiOptions
) => {
  return useApi<CorrespondenceAttachment[]>(
    async () => {
      if (!id) {
        throw new Error('Correspondence ID is required');
      }

      validateObjectId(id, 'Correspondence ID');


      const response = await apiClient.correspondences.getAttachments(id);

      return normalizeSingleResponse<CorrespondenceAttachment[]>(response);
    },
    {
      immediate: !!id,
      dependencies: [id],
      rateLimitKey: `correspondence_attachments_${id}`,
      ...hookOptions
    }
  );
};

/**
 * V3: Get AI summary for a correspondence
 * @param id - Correspondence ID
 */
export const useCorrespondenceAI = (
  id: string | null,
  hookOptions?: UseApiOptions
) => {
  return useApi<CorrespondenceAI>(
    async () => {
      if (!id) {
        throw new Error('Correspondence ID is required');
      }

      validateObjectId(id, 'Correspondence ID');


      const response = await apiClient.correspondences.getAISummary(id);

      return normalizeSingleResponse<CorrespondenceAI>(response);
    },
    {
      immediate: !!id,
      dependencies: [id],
      rateLimitKey: `correspondence_ai_${id}`,
      ...hookOptions
    }
  );
};

/**
 * V3: Get audit trail for a correspondence
 * @param id - Correspondence ID
 * @param page - Page number (optional)
 * @param limit - Items per page (optional)
 */
export const useCorrespondenceAudit = (
  id: string | null,
  page?: number,
  limit?: number,
  hookOptions?: UseApiOptions
) => {
  return useApi<PaginatedResponse<CorrespondenceAudit>>(
    async () => {
      if (!id) {
        throw new Error('Correspondence ID is required');
      }

      validateObjectId(id, 'Correspondence ID');


      const response = await apiClient.correspondences.getAuditTrail(id, { page, limit });

      return normalizePaginatedResponse<CorrespondenceAudit>(response);
    },
    {
      immediate: !!id,
      dependencies: [id, page, limit],
      rateLimitKey: `correspondence_audit_${id}_${page}_${limit}`,
      ...hookOptions
    }
  );
};

/**
 * V3: Get correspondence with all related data in one call
 * @param id - Correspondence ID
 */
export const useCorrespondenceFull = (
  id: string | null,
  hookOptions?: UseApiOptions
) => {
  return useApi<CorrespondenceFull>(
    async () => {
      if (!id) {
        throw new Error('Correspondence ID is required');
      }

      validateObjectId(id, 'Correspondence ID');


      const response = await apiClient.correspondences.getFull(id);

      return normalizeSingleResponse<CorrespondenceFull>(response);
    },
    {
      immediate: !!id,
      dependencies: [id],
      rateLimitKey: `correspondence_full_${id}`,
      ...hookOptions
    }
  );
};